import { coerceText } from '../pages/opportunities/utils/coerce-text';

export const initialState = {
    isDarkMode: false,
    mainLayout: 'app',
    theme: 'light',
    menu: 'vertical',
    layout: 'full',
    rtlClass: 'ltr',
    animation: '',
    navbar: 'navbar-static',
    locale: 'en',
    sidebar: false,
    languageList: [
        { code: 'zh', name: 'Chinese' },
        { code: 'da', name: 'Danish' },
        { code: 'en', name: 'English' },
        { code: 'fr', name: 'French' },
        { code: 'de', name: 'German' },
        { code: 'el', name: 'Greek' },
        { code: 'hu', name: 'Hungarian' },
        { code: 'it', name: 'Italian' },
        { code: 'ja', name: 'Japanese' },
        { code: 'pl', name: 'Polish' },
        { code: 'pt', name: 'Portuguese' },
        { code: 'ru', name: 'Russian' },
        { code: 'es', name: 'Spanish' },
        { code: 'sv', name: 'Swedish' },
        { code: 'tr', name: 'Turkish' },
        { code: 'ae', name: 'Arabic' },
    ],
    isShowMainLoader: true,
    semidark: false,
};

type ReducerState = typeof initialState & { direction?: string };
type ReducerAction = { type: string; payload?: unknown };

function resolveDarkMode(theme: string, state: ReducerState): boolean {
    if (theme === 'light') {
        return false;
    }
    if (theme === 'dark') {
        return true;
    }
    if (theme === 'system') {
        return !!(window.matchMedia?.('(prefers-color-scheme: dark)')?.matches);
    }
    return state.isDarkMode || false;
}

function enableDarkModeClass(): void {
    document.querySelector('body')?.classList.add('dark');
}

function disableDarkModeClass(): void {
    document.querySelector('body')?.classList.remove('dark');
}

const actionHandlers: Record<string, (state: ReducerState, payload: unknown) => ReducerState> = {
    toggleDirection: (state, payload) => {
        const direction = coerceText(payload ?? 'ltr');
        localStorage.setItem('direction', direction);
        document.querySelector('html')?.setAttribute('dir', direction);
        return { ...state, direction };
    },
    toggleMainLoader: (state, payload) => ({
        ...state,
        isShowMainLoader: Boolean(payload),
    }),
    setMainLayout: (state, payload) => ({
        ...state,
        mainLayout: coerceText(payload ?? state.mainLayout),
    }),
    toggleTheme: (state, payload) => {
        const theme = coerceText(payload || state.theme);
        localStorage.setItem('theme', theme);
        const isDarkMode = resolveDarkMode(theme, state);
        if (isDarkMode) {
            enableDarkModeClass();
        } else {
            disableDarkModeClass();
        }
        return { ...state, ...{ theme, isDarkMode } };
    },
    toggleMenu: (state, payload) => {
        const menu = coerceText(payload || state.menu);
        localStorage.setItem('menu', menu);
        return { ...state, ...{ sidebar: false, menu } };
    },
    toggleLayout: (state, payload) => {
        const layout = coerceText(payload || state.layout);
        localStorage.setItem('layout', layout);
        return { ...state, ...{ layout } };
    },
    toggleRTL: (state, payload) => {
        const rtlClass = coerceText(payload || state.rtlClass);
        localStorage.setItem('rtlClass', rtlClass);
        document.querySelector('html')?.setAttribute('dir', rtlClass || 'ltr');
        return { ...state, ...{ rtlClass } };
    },
    toggleAnimation: (state, payload) => {
        const animation =
            typeof payload === 'string' ? payload.trim() : coerceText(state.animation ?? '');
        localStorage.setItem('animation', animation);
        if (animation) {
            const eleanimation: Element | null = document.querySelector('.animation');
            eleanimation?.classList.add('animate__animated');
            eleanimation?.classList.add(animation);
        }
        return { ...state, animation };
    },
    toggleNavbar: (state, payload) => {
        const navbar = coerceText(payload || state.navbar);
        localStorage.setItem('navbar', navbar);
        return { ...state, ...{ navbar } };
    },
    toggleSemidark: (state, payload) => {
        const semidark = Boolean(payload);
        localStorage.setItem('semidark', String(semidark));
        return { ...state, semidark };
    },
    toggleLocale: (state, payload) => {
        const locale = coerceText(payload || state.locale);
        localStorage.setItem('i18n_locale', locale);
        return { ...state, ...{ locale } };
    },
    toggleSidebar: (state) => ({ ...state, ...{ sidebar: !state.sidebar } }),
};

export function indexReducer(state = initialState, action: ReducerAction) {
    const handler = actionHandlers[action.type];
    if (handler) {
        return handler(state, action.payload);
    }
    return state;
}
